import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import { sectionHeadingSx } from '../utils/editor-theme';
import { MAXIMUM_SPACING_METRES } from '../utils/desk-groups';
import { arrangementsFor, type PodLayout } from '../utils/pod-layout';

interface Props {
  readonly layout: PodLayout;
  /** How many desks the group has, which is what limits the arrangements it can be put in. */
  readonly seats: number;
  readonly onChange: (layout: PodLayout) => void;
}

const keyOf = (columns: number, rows: number): string => `${columns}x${rows}`;

/**
 * Re-arranging a group of desks that is already on the plan.
 *
 * The same three decisions the picker offers before a group is stamped — how it is arranged,
 * how far apart the desks sit, and whether the rows are back to back — offered again afterwards,
 * because they are not decisions anyone gets right from a menu before seeing the desks in the
 * room. Until now the only way to change them was to delete the group and stamp another, and a
 * stamped desk is a bookable workplace: deleting six desks to re-space them takes six sets of
 * reservations with it.
 *
 * Only arrangements that use every desk are offered. A group of six is 6x1, 3x2, 2x3 or 1x6;
 * "4 x 2" would mean inventing two desks nobody has booked or destroying two somebody has, and
 * neither is something a dropdown should do quietly.
 */
const DeskGroupLayoutField = ({ layout, seats, onChange }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const arrangements = arrangementsFor(seats);

  const spacingLimits = { min: 0, max: MAXIMUM_SPACING_METRES, step: 0.1 };

  const onSpacingChange = (key: 'gapMetres' | 'aisleMetres' | 'backGapMetres') => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const metres = Number(event.target.value);

    if (Number.isFinite(metres) && metres >= 0 && metres <= MAXIMUM_SPACING_METRES) {
      onChange({ ...layout, [key]: metres });
    }
  };

  return (
    <Stack spacing={2}>
      <Typography sx={sectionHeadingSx}>{t('floorplan-editor-desk-group-layout')}</Typography>

      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {t('floorplan-editor-desk-group-seats', { count: seats })}
      </Typography>

      <TextField
        select
        size="small"
        label={String(t('floorplan-editor-desk-group-arrangement'))}
        value={keyOf(layout.columns, layout.rows)}
        onChange={(event): void => {
          const chosen = arrangements
            .find((arrangement) => keyOf(arrangement.columns, arrangement.rows) === event.target.value);

          if (chosen) {
            onChange({
              ...layout,
              columns: chosen.columns,
              rows: chosen.rows,
              // A single row has no other row to sit back to back with, so the setting is
              // dropped rather than kept and hidden — it would come back on the next change.
              backToBack: chosen.rows > 1 && layout.backToBack,
            });
          }
        }}
      >
        {arrangements.map((arrangement) => (
          <MenuItem key={keyOf(arrangement.columns, arrangement.rows)} value={keyOf(arrangement.columns, arrangement.rows)}>
            {t('floorplan-editor-desk-group-grid', {
              columns: arrangement.columns,
              rows: arrangement.rows,
            })}
          </MenuItem>
        ))}
      </TextField>

      {layout.rows > 1 && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={layout.backToBack}
          onChange={(_, next: boolean | null): void => {
            if (next !== null) {
              onChange({ ...layout, backToBack: next });
            }
          }}
        >
          <ToggleButton value={false} aria-label={String(t('floorplan-editor-desk-group-aisles'))}>
            <Typography variant="caption">{t('floorplan-editor-desk-group-aisles')}</Typography>
          </ToggleButton>
          <ToggleButton value aria-label={String(t('floorplan-editor-desk-group-back-to-back'))}>
            <Typography variant="caption">{t('floorplan-editor-desk-group-back-to-back')}</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          type="number"
          inputProps={spacingLimits}
          label={String(t('floorplan-editor-desk-group-gap'))}
          value={layout.gapMetres}
          onChange={onSpacingChange('gapMetres')}
          // A single column has nothing beside it, so there is no gap between desks to set.
          disabled={layout.columns < 2}
        />
        {/* One distance between two rows, named for what is on the other side of it: an aisle
            people walk down, or the strip between two desks' backs. A bench's rows used to have
            no field at all, which is to say no way to part them. */}
        <TextField
          size="small"
          type="number"
          inputProps={spacingLimits}
          label={String(t(layout.backToBack
            ? 'floorplan-editor-desk-group-back-gap'
            : 'floorplan-editor-desk-group-aisle'))}
          value={layout.backToBack ? layout.backGapMetres : layout.aisleMetres}
          onChange={onSpacingChange(layout.backToBack ? 'backGapMetres' : 'aisleMetres')}
          // A single row has no second row to sit at any distance from.
          disabled={layout.rows < 2}
          helperText={layout.backToBack
            ? String(t('floorplan-editor-desk-group-back-gap-hint'))
            : undefined}
        />
      </Stack>
    </Stack>
  );
};

export default DeskGroupLayoutField;
